import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSpinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function IntentsCommandList({
  placeholder = 'Filter intents...',
  selected = [],
  onSelect,
  multiselect,
}: BaseCommandListProps<false, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Labels)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter((intent) => intent.name.toLowerCase().includes(normalizedQuery))
  }, [data, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  const handleClear = useCallback(() => {
    onSelect([])
  }, [onSelect])

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      <CommandInput
        placeholder={placeholder}
        autoFocus
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="py-1">
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {selected.length > 0 && (
            <CommandItem onSelect={handleClear} className="gap-2">
              Clear
            </CommandItem>
          )}
          {visibleItems?.map((scorecard) => (
            <CommandCheckItem
              key={scorecard.id}
              value={scorecard.id}
              keywords={[scorecard.name]}
              selected={selected.includes(scorecard.id)}
              onSelect={() => handleToggleItem(scorecard.id)}
              multiselect={multiselect}
            >
              {scorecard.name}
            </CommandCheckItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
